import Vue from 'vue'
import VueRouter from 'vue-router'
import moment from 'moment'
import { jwtDecode } from 'jwt-decode'
import { getCookie } from 'tiny-cookie'
import { isEmpty } from 'lodash'
import { appTitle } from '../shared/constants'
import { resolveVue3Path, clearPreferredFrontend, setPreferredFrontend, hasVue3Equivalent, honoursPreferredFrontend } from '@/shared/frontendModeMap'
import i18n from '@/i18n'
import store from '@/store'
import { setFaroView } from '@/lib/faro'

Vue.use(VueRouter)

const router = new VueRouter({
  routes: [
    {
      path: '/',
      name: 'Home',
      redirect: '/desktops',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: 'desktops',
          name: 'desktops',
          component: () => import('@/pages/Desktops.vue'),
          meta: {
            title: i18n.t('router.titles.desktops'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        },
        {
          path: 'desktops/new',
          name: 'desktopsnew',
          component: () => import('@/pages/DesktopNew.vue'),
          meta: {
            title: i18n.t('router.titles.new_desktop'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/domain',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: 'edit',
          name: 'domainedit',
          component: () => import('@/pages/DomainEdit.vue'),
          meta: {
            title: i18n.t('router.titles.edit-domain'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/booking',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: ':type/:id',
          name: 'booking',
          component: () => import('@/pages/Booking.vue'),
          meta: {
            title: i18n.t('router.titles.booking'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        },
        {
          path: 'summary',
          name: 'bookingsummary',
          component: () => import('@/pages/BookingsSummary.vue'),
          meta: {
            title: i18n.t('router.titles.booking'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/planning',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'Planning',
          component: () => import('@/pages/Planning.vue'),
          meta: {
            title: i18n.t('router.titles.planning'),
            allowedRoles: ['admin', 'manager']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/templates',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'templates',
          component: () => import('@/pages/Templates.vue'),
          meta: {
            title: i18n.t('router.titles.templates'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'new',
          name: 'templatenew',
          component: () => import('@/pages/TemplateNew.vue'),
          meta: {
            title: i18n.t('router.titles.new_template'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'duplicate',
          name: 'templateduplicate',
          component: () => import('@/pages/TemplateNew.vue'),
          meta: {
            title: i18n.t('router.titles.duplicate_template'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/rdp',
      name: 'Rdp',
      component: () => import('@/views/Rdp.vue')
    },
    {
      path: '/deployments',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'deployments',
          component: () => import('@/pages/Deployments.vue'),
          meta: {
            title: i18n.t('router.titles.deployment'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'new',
          name: 'deploymentsnew',
          component: () => import('@/pages/DeploymentNew.vue'),
          meta: {
            title: i18n.t('router.titles.new_deployment'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/deployment/:id',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'deployment_desktops',
          component: () => import('@/pages/Deployment.vue'),
          meta: {
            title: i18n.t('router.titles.deployment'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'videowall',
          name: 'deployment_videowall',
          component: () => import('@/pages/DeploymentVideowall.vue'),
          meta: {
            title: i18n.t('router.titles.deployment_videowall'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'edit',
          name: 'deploymentEdit',
          component: () => import('@/pages/DeploymentEdit.vue'),
          meta: {
            title: i18n.t('router.titles.edit-deployment'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/profile',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: ':action?',
          name: 'profile',
          component: () => import('@/pages/Profile.vue'),
          meta: {
            title: i18n.t('router.titles.profile'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/media',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'media',
          component: () => import('@/pages/Media.vue'),
          meta: {
            title: i18n.t('router.titles.media'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'new',
          name: 'medianew',
          component: () => import('@/pages/MediaNew.vue'),
          meta: {
            title: i18n.t('router.titles.new_media'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        },
        {
          path: 'desktop/new',
          name: 'newfrommedia',
          component: () => import('@/pages/NewFromMedia.vue'),
          meta: {
            title: i18n.t('router.titles.new_desktop'),
            allowedRoles: ['admin', 'manager', 'advanced']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/',
      name: 'RecycleBins',
      redirect: '/recycleBins',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: 'recycleBins',
          name: 'recycleBins',
          component: () => import('@/pages/RecycleBins.vue'),
          meta: {
            title: i18n.t('router.titles.recycleBin'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        },
        {
          path: '/recyclebin/:id',
          name: 'recycleBin',
          component: () => import('@/pages/RecycleBin.vue'),
          meta: {
            title: i18n.t('router.titles.recycleBin'),
            allowedRoles: ['admin', 'manager', 'advanced', 'user']
          }
        }
      ],
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/reset-password',
      component: () => import('@/pages/ResetPassword.vue'),
      name: 'ResetPassword',
      meta: {
        title: i18n.t('router.titles.password'),
        requiresAuth: true
      }
    },
    {
      path: '/forgot-password',
      component: () => import('@/pages/ForgotPassword.vue'),
      name: 'ForgotPassword',
      meta: {
        title: i18n.t('router.titles.forgot-password')
      }
    },
    {
      path: '/verify-email',
      component: () => import('@/pages/VerifyEmail.vue'),
      name: 'VerifyEmail',
      meta: {
        title: i18n.t('router.titles.verify-email'),
        requiresAuth: true
      }
    },
    {
      path: '/disclaimer',
      component: () => import('@/pages/Disclaimer.vue'),
      name: 'Disclaimer',
      meta: {
        title: i18n.t('router.titles.disclaimer')
      }
    },
    {
      path: '/export-user',
      component: () => import('@/pages/ExportUser.vue'),
      name: 'ExportUser',
      meta: {
        title: i18n.t('router.titles.export-user')
      }
    },
    {
      path: '/error/:code',
      name: 'Error',
      component: () => import('@/views/Error.vue'),
      meta: {
        title: i18n.t('router.titles.error')
      }
    },
    {
      path: '*',
      name: 'NotFound',
      component: () => import('@/views/NotFound.vue'),
      meta: {
        title: i18n.t('router.titles.not_found')
      }
    }
  ],
  mode: 'history'
})

router.beforeEach(async (to, from, next) => {
  moment.locale(localStorage.language)
  document.title = to.meta.title ? `${appTitle} - ${to.meta.title}` : appTitle
  const session = store.getters.getSession
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // No session yet
    if (!session) {
      const authCookie = getCookie('authorization')
      if (!authCookie) {
        if (['VerifyEmail', 'ResetPassword'].includes(to.name)) {
          if (to.query.token) {
            next()
            return
          }
        }

        window.location.pathname = '/login'
        return
      }

      store.dispatch('loginSuccess', authCookie)
      return
    }

    const sessionData = jwtDecode(session)
    // Handle user registration
    if (sessionData.type === 'register' || sessionData.type === 're-register') {
      window.location.pathname = '/register'
      return
    } else if (sessionData.type === 'category-select') {
      window.location.pathname = '/login'
      return
    }

    // Skip session renewal for special token types (disclaimer, email verification, password reset)
    const skipRenewalTypes = [
      'disclaimer-acknowledgement-required',
      'email-verification-required',
      'email-verification',
      'password-reset-required',
      'password-reset'
    ]

    if (!skipRenewalTypes.includes(sessionData.type)) {
      // Renew the session 1 minute before it expires
      const now = Date.now()
      const timeDrift = store.getters.getTimeDrift
      const adjustedNow = now + (Math.abs(timeDrift) < 86400000 ? timeDrift : 0)

      if (
        sessionData.session_id !== 'isardvdi-service' &&
        sessionData.exp &&
        adjustedNow > sessionData.exp * 1000 - 60000
      ) {
        // If the token is expired or expiring soon, try to renew it
        if (!await store.dispatch('renew')) {
          console.warn('The session was expired and could not be renewed')
          store.dispatch('logout')
          window.location.pathname = '/login'
          return
        }
      }
    }

    store.dispatch('saveNavigation', { url: to })
    store.dispatch('fetchUser')

    // Logged in without requirements
    if (!to.query.token && !sessionData.type) {
      if (
        to.meta.allowedRoles &&
        to.meta.allowedRoles.includes(store.getters.getUser.role_id)
      ) {
        store.dispatch('openSocket', {})
        if (isEmpty(store.getters.getConfig)) {
          await store.dispatch('fetchConfig')
        }
        // A deployment can leave `all`/`hidden` after browsers already stored a preference.
        if (!honoursPreferredFrontend(store.getters.getConfig.frontendMode)) {
          clearPreferredFrontend()
        }
        if (store.getters.getConfig.frontendMode === 'actual') {
          const target = resolveVue3Path(to) || '/frontend/desktops'
          window.location.assign(target)
          return
        }
        if (honoursPreferredFrontend(store.getters.getConfig.frontendMode) && hasVue3Equivalent(to.name)) {
          setPreferredFrontend('vue2')
        }
        if (!store.getters.getMaxTime) {
          store.dispatch('fetchMaxTime')
        }
        next()
      } else {
        store.dispatch('saveNavigation', { url: from })
        next({ name: 'desktops' })
      }
    // Requires disclaimer acceptance, will be redirected
    } else if (
      to.name !== 'Disclaimer' &&
      ['disclaimer-acknowledgement-required'].includes(sessionData.type)
    ) {
      router.push({ name: 'Disclaimer' })
    // Requires email verification, will be redirected
    } else if (
      to.name !== 'VerifyEmail' &&
      ['email-verification-required', 'email-verification'].includes(
        sessionData.type
      )
    ) {
      router.push({ name: 'VerifyEmail' })
    // Requires password reset, will be redirected
    } else if (
      to.name !== 'ResetPassword' &&
      ['password-reset-required', 'password-reset'].includes(
        sessionData.type
      )
    ) {
      router.push({ name: 'ResetPassword' })
    // Requires user migration, will be redirected
    } else if (
      to.name !== 'ExportUser' &&
      ['user-migration-required'].includes(
        sessionData.type
      )
    ) {
      window.location.assign('/export-user')
    } else {
      next()
    }
  } else {
    if (
      to.name === 'Login' &&
      getCookie('authorization') &&
      jwtDecode(getCookie('authorization')).type === 'category-select'
    ) {
      window.location.pathname = '/'
    } else {
      store.dispatch('saveNavigation', { url: to })
      next()
    }
  }
})

// Keep Faro's view.name in sync with the active route so `view_name` in
// Loki maps to the URL path the user is actually on.
router.afterEach((to) => {
  setFaroView(to.path)
})

export default router
